import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Building2, ChevronDown, ClipboardList, FileCheck2, LayoutDashboard, LogOut, ScrollText, ShieldCheck, Store, Users, UsersRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, ROLE_LABELS } from "@/lib/api";
import { NotificationBell } from "@/components/NotificationBell";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const NAV = {
  admin: [["/", "Dashboard", LayoutDashboard], ["/patients", "Patients", Users], ["/reports", "Reports", FileCheck2], ["/marketplace", "Marketplace", Store], ["/audit", "Audit Trail", ScrollText], ["/organizations", "Clinics", Building2], ["/users", "Users", UsersRound]],
  doctor: [["/", "Review Queue", LayoutDashboard], ["/patients", "Patients", Users], ["/reports", "Reports", FileCheck2], ["/marketplace", "Marketplace", Store], ["/audit", "Audit Trail", ScrollText]],
  staff: [["/", "Dashboard", LayoutDashboard], ["/patients", "Patients", Users], ["/reports", "Reports", FileCheck2], ["/marketplace", "Marketplace", Store]],
  lab: [["/", "Lab Intake", LayoutDashboard], ["/patients", "Patients", Users], ["/marketplace", "Marketplace", Store]],
  patient: [["/", "My Health", LayoutDashboard], ["/reports", "My Reports", FileCheck2], ["/marketplace", "Marketplace", Store]],
  nutritionist: [["/", "Dashboard", LayoutDashboard], ["/patients", "Clients", Users], ["/reports", "Shared Reports", FileCheck2], ["/marketplace", "Marketplace", Store]],
  insurance: [["/", "Dashboard", LayoutDashboard], ["/patients", "Consented Members", Users], ["/reports", "Released Summaries", ClipboardList], ["/marketplace", "Marketplace", Store]],
  dx: [["/", "My Brand", LayoutDashboard], ["/marketplace", "Marketplace", Store]],
  medtech: [["/", "My Brand", LayoutDashboard], ["/marketplace", "Marketplace", Store]],
};

export function AppShell({ children }) {
  const { user, logout, login } = useAuth();
  const navigate = useNavigate();
  const [demos, setDemos] = useState([]);
  useEffect(() => { api.get("/auth/demo-accounts").then((r) => setDemos(r.data)).catch(() => {}); }, []);

  const switchRole = async (d) => {
    try { await login(d.email, d.password); navigate("/"); toast.success(`Switched to ${ROLE_LABELS[d.role]} view`); } catch { toast.error("Could not switch role"); }
  };
  const items = NAV[user.role] || NAV.patient;

  return (
    <div className="min-h-screen bg-slate-50">
      <header data-testid="global-nav-header" className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-6">
          <Link to="/" className="flex items-center gap-2.5 shrink-0" data-testid="brand-link">
            <img src="/carestop-logo-header.png" alt="CareStop" className="h-12 w-auto rounded-lg" />
          </Link>
          <nav className="hidden md:flex items-center gap-1 flex-1">
            {items.map(([to, label, Icon]) => (
              <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-teal-50 text-teal-800" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
                <Icon className="w-4 h-4" /> {label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <span data-testid="dpdp-status-indicator" className="hidden sm:inline-flex badge-pill bg-green-50 text-green-800 border-green-200"><ShieldCheck className="w-3.5 h-3.5" /> DPDP-aware</span>
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="role-switcher-dropdown" className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full border border-slate-200 hover:border-teal-300 bg-white transition-colors">
                  <span className="w-7 h-7 rounded-full bg-teal-100 text-teal-800 grid place-items-center text-xs font-bold">{(user.name || "U")[0]}</span>
                  <span className="hidden sm:block text-left leading-tight">
                    <span className="block text-xs font-semibold text-slate-900">{user.name}</span>
                    <span data-testid="active-role-badge" className="block text-[10px] uppercase tracking-wider text-teal-700 font-semibold">{ROLE_LABELS[user.role]}{user.organization && ` · ${user.organization}`}</span>
                  </span>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 bg-white" data-testid="user-profile-menu">
                <DropdownMenuLabel className="text-xs text-slate-500">{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="eyebrow">Switch demo role</DropdownMenuLabel>
                {demos.filter((d) => d.role !== user.role).map((d) => (
                  <DropdownMenuItem key={d.role} data-testid={`switch-role-${d.role}`} onClick={() => switchRole(d)} className="cursor-pointer">
                    <span className="font-medium">{ROLE_LABELS[d.role]}</span><span className="ml-auto text-xs text-slate-400">{d.label}</span>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="logout-button" onClick={() => { logout(); navigate("/login"); }} className="cursor-pointer text-rose-700"><LogOut className="w-4 h-4 mr-2" /> Sign out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <nav className="md:hidden flex overflow-x-auto gap-1 px-3 pb-2">
          {items.map(([to, label, Icon]) => (
            <NavLink key={to} to={to} end={to === "/"} className={({ isActive }) => `flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${isActive ? "bg-teal-50 text-teal-800" : "text-slate-600"}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">{children}</main>
    </div>
  );
}
